import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Tag } from 'primereact/tag';
import axiosInstance from '../services/axiosInstance';
import { useAllProducts } from '../hooks/useAllProducts';
import ExpiryInput from './ExpiryInput';
import { localYmd, toYmd, ymdToDate } from '../utils/dateOnly';
import { handleApiError, handleApiSuccess } from './apiErrorHandler';
import { shrinkImage } from '../utils/shrinkImage';
import { rs } from './AIGrnUsage';

const FLAG_TEXT = {
  product_unconfirmed: 'Check product',
  qty_inferred: 'Qty worked out from value',
  qty_from_batch_text: 'Qty from batch text',
  price_inferred: 'Price worked out',
  value_inferred: 'Value worked out',
  solved_from_row: 'Re-solved from row',
  free_from_batch_text: 'Free qty from batch text',
  line_does_not_add_up: 'Qty x price does not match value',
  price_decimal_fixed: 'Price decimal point corrected',
  selling_below_cost: 'Selling price on file is below this cost',
  no_expiry: 'No expiry',
  description_unread: 'Product name not read - choose the product',
  pack_from_retail: 'Pack size worked out from retail price',
  pack_size_conflict: 'Printed pack size differs from retail / selling price',
};
const money = (v) => (v == null ? '' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const Field = ({ label, error, children, className = '', style }) => (
  <div className={`flex flex-column gap-1 ${className}`} style={style}>
    <label style={{ color: error ? '#d32f2f' : undefined, fontWeight: error ? 600 : undefined }}>{label}</label>
    {children}
    {error && <small style={{ color: '#d32f2f' }}><i className="pi pi-exclamation-circle mr-1" />{error}</small>}
  </div>
);

const validDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v || '') && !Number.isNaN(Date.parse(v));

const PurchaseScanDialog = ({ visible, onHide, onCreated }) => {
  const { data: products = [], refetch: refetchProducts } = useAllProducts({ enabled: visible });
  const [suppliers, setSuppliers] = useState([]);
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ supplier: null, invoice_no: '', credit_days: null });
  const [lines, setLines] = useState([]);
  const [showErrors, setShowErrors] = useState(false);
  const fileRef = useRef(null);
  const pageFileRef = useRef(null);
  const [extraPages, setExtraPages] = useState([]);        // further pages of the same invoice: {id, page_no, status, image_url, error}
  const [activePage, setActivePage] = useState(0);         // 0 = first page, n = extraPages[n - 1]
  const [billTotal, setBillTotal] = useState(null);
  const merged = useRef(new Set());                         // extra pages whose lines are already in the list
  const [useAi, setUseAi] = useState(false);                    // per bill, always starts off
  const [aiStatus, setAiStatus] = useState(null);             // {enabled, usable, unusable_reason, balance_lkr, ...} for AI-read GRNs
  const [pageCosts, setPageCosts] = useState({});             // scan id -> AI cost (Rs.) of each extra page
  const [pending, setPending] = useState([]);                // bills waiting: held phone uploads, being read, read but not yet a GRN
  const [onePage, setOnePage] = useState(true);               // several photos picked for 'upload only' are pages of ONE bill
  const heldFileRef = useRef(null);
  const [dupMsg, setDupMsg] = useState('');                    // this supplier + invoice number is already on file
  const resumeExtra = useRef([]);                           // already-read further pages of a reopened bill: [{id, page_no, lines, total, cost}]

  useEffect(() => {
    if (!visible) return;
    setScan(null); setLines([]); setShowErrors(false); setForm({ supplier: null, invoice_no: '', credit_days: null });
    setExtraPages([]); setActivePage(0); setBillTotal(null); merged.current = new Set();
    setPageCosts({}); setUseAi(false);
    axiosInstance.get('/api/suppliers/?all=1').then((r) => setSuppliers(r.data)).catch(handleApiError);
    axiosInstance.get('/api/ai-grn/status/').then((r) => setAiStatus(r.data)).catch(() => setAiStatus(null));
    resumeExtra.current = [];
    loadPending();
  }, [visible]);

  const loadPending = () => axiosInstance.get('/api/purchase-scans/pending/').then((r) => setPending(r.data)).catch(() => {});

  // poll while the server reads the bill
  useEffect(() => {
    if (!scan || !['queued', 'processing'].includes(scan.status)) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await axiosInstance.get(`/api/purchase-scans/${scan.id}/`);
        if (!['queued', 'processing'].includes(data.status)) setScan(data);
      } catch (e) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(t);
  }, [scan]);

  // when the bill is ready, copy its fields into the editable form
  useEffect(() => {
    if (scan?.status !== 'ready' || !scan.parsed) return;
    const p = scan.parsed;
    setForm({ supplier: p.supplier_id || null, invoice_no: p.invoice_no || '', credit_days: p.credit_days || null });
    setShowErrors(false);
    const more = resumeExtra.current; resumeExtra.current = [];
    setBillTotal([...more].reverse().map((m) => m.total).find((t) => t != null) ?? p.total ?? null);
    setPageCosts(Object.fromEntries(more.filter((m) => m.cost).map((m) => [m.id, m.cost])));
    const mk = (list, page) => (list || []).map((l) => ({ ...l, page, selling_price: l.selling_on_file ?? null, units_per_pack: l.pack_size || null }));
    setLines([...mk(p.lines, 1), ...more.flatMap((m) => mk(m.lines, m.page_no))]);
  }, [scan?.id, scan?.status]);

  // read further pages: poll each until ready, then append its lines to the review list
  useEffect(() => {
    const waiting = extraPages.filter((pg) => ['queued', 'processing'].includes(pg.status));
    if (!waiting.length) return undefined;
    const t = setInterval(async () => {
      for (const pg of waiting) {
        try {
          const { data } = await axiosInstance.get(`/api/purchase-scans/${pg.id}/`);
          if (['queued', 'processing'].includes(data.status)) continue;
          setExtraPages((list) => list.map((x) => (x.id === pg.id ? { ...x, status: data.status, error: data.error } : x)));
          if (data.status === 'ready' && !merged.current.has(pg.id)) {
            merged.current.add(pg.id);
            if (data.parsed?.ai?.used) setPageCosts((c) => ({ ...c, [pg.id]: data.parsed.ai.cost_lkr }));
            const found = data.parsed?.lines || [];
            setLines((ls) => [...ls, ...found.map((l) => ({ ...l, page: data.page_no, selling_price: l.selling_on_file ?? null, units_per_pack: l.pack_size || null }))]);
            if (data.parsed?.total != null) setBillTotal(data.parsed.total);   // the totals block is normally on the last page
            if (!found.length) handleApiError({ Error: `No item lines were found on page ${data.page_no}` });
          }
        } catch (e) { /* keep polling */ }
      }
    }, 2500);
    return () => clearInterval(t);
  }, [extraPages]);

  const uploadError = (e) => (e?.response?.status === 413
    ? handleApiError({ Error: 'The photo is too large to upload (limit 10 MB). Try again with a smaller photo.' })
    : handleApiError(e));

  const addPage = async (file) => {
    if (!file || !scan) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('image', await shrinkImage(file));   // phone photos are 3-5 MB; send a ~2400 px JPEG
      fd.append('parent', scan.id);
      const { data } = await axiosInstance.post('/api/purchase-scans/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setExtraPages((list) => [...list, { id: data.id, page_no: data.page_no, status: data.status, image_url: data.image_url }]);
      setActivePage(extraPages.length + 1);
    } catch (e) { uploadError(e); } finally { setBusy(false); if (pageFileRef.current) pageFileRef.current.value = ''; }
  };
  const readingPage = extraPages.some((pg) => ['queued', 'processing'].includes(pg.status));

  const productOptions = useMemo(() => {
    const opts = products.map((p) => ({ label: `${p.name}  (${p.code})`, value: p.id }));
    const known = new Set(products.map((p) => p.id));
    lines.forEach((l) => (l.candidates || []).forEach((c) => {
      if (!known.has(c.product_id)) { known.add(c.product_id); opts.push({ label: `${c.name}${c.code ? `  (${c.code})` : ''}`, value: c.product_id }); }
    }));
    return opts;
  }, [products, lines]);

  // the product list is cached for the whole session; if the scan names a product we don't have, reload it
  useEffect(() => {
    if (!lines.length || !products.length) return;
    const known = new Set(products.map((p) => p.id));
    if (lines.some((l) => (l.candidates || []).some((c) => !known.has(c.product_id)))) refetchProducts();
  }, [lines.length, products.length]);

  const productInfo = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  // earliest expiry the pharmacy accepts: today + block_invoice_days_before_expiry (Organization settings)
  const minExpiryDate = useMemo(() => {
    let days = 0;
    try { days = JSON.parse(localStorage.getItem('organizationSettings') || '{}').block_invoice_days_before_expiry ?? 0; } catch (e) { /* default */ }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + days);
    return d;
  }, [visible]);

  // choosing a product (list or suggestion button) pulls its selling price and pack size from the price list
  const selectProduct = async (i, pid) => {
    setLine(i, { product_id: pid });
    if (!pid) return;
    try {
      const { data } = await axiosInstance.get(`/api/products/${pid}/active-pricelist/`);
      setLines((ls) => ls.map((l, k) => {
        if (k !== i || l.product_id !== pid) return l;
        const sell = data.selling_price != null ? Number(data.selling_price) : null;
        // the bill's retail price is per pack and our selling price per unit: their ratio is the pack size
        let hint = null;
        if (sell > 0 && l.retail_price > 0) {
          const ratio = l.retail_price / sell;
          const n = Math.round(ratio);
          if (n >= 1 && Math.abs(ratio - n) <= 0.005 * n) hint = n;
        }
        const printed = l.units_per_pack || null;
        const units = printed || data.units_per_pack || hint || null;
        const flags = (l.flags || []).filter((f) => !['product_unconfirmed', 'pack_from_retail', 'pack_size_conflict'].includes(f));
        if (hint && !printed && !data.units_per_pack) flags.push('pack_from_retail');
        else if (hint && units !== hint) flags.push('pack_size_conflict');
        return { ...l, selling_price: sell, selling_on_file: sell, units_per_pack: units, pack_hint: hint, flags };
      }));
    } catch (e) { handleApiError(e); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('image', await shrinkImage(file));
      fd.append('use_ai', useAi && aiStatus?.usable ? '1' : '0');
      const { data } = await axiosInstance.post('/api/purchase-scans/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (data.duplicate) handleApiSuccess({ data: { message: 'This photo was uploaded before; showing the earlier result.' } });
      setScan(data);
    } catch (e) { uploadError(e); } finally { setBusy(false); }
  };

  // phone upload: keep the photo(s) now, read them later on a PC. Several photos can be pages of one bill.
  const uploadHeld = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      let parent = null, saved = 0;
      for (const f of files) {
        const fd = new FormData();
        fd.append('image', await shrinkImage(f)); fd.append('defer', '1');
        if (onePage && parent) fd.append('parent', parent);
        const { data } = await axiosInstance.post('/api/purchase-scans/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (onePage && !parent) parent = data.id;
        saved += 1;
      }
      handleApiSuccess(`${saved} photo${saved > 1 ? 's' : ''} saved. Open Scan Bill on a computer to read ${saved > 1 && onePage ? 'it' : 'them'}.`);
      loadPending();
    } catch (e) { uploadError(e); } finally { setBusy(false); if (heldFileRef.current) heldFileRef.current.value = ''; }
  };

  // reopen a waiting bill: read it first if it was only uploaded, bring back its pages
  const openBill = async (row) => {
    setBusy(true);
    try {
      if (row.status === 'uploaded' || row.status === 'failed') {
        await axiosInstance.post(`/api/purchase-scans/${row.id}/process/`, { use_ai: useAi && aiStatus?.usable });
      }
      const { data } = await axiosInstance.get(`/api/purchase-scans/${row.id}/`);
      const pages = data.pages || [];
      const read = [];
      for (const pg of pages.filter((x) => x.page_no > 1 && x.status === 'ready')) {
        const { data: d } = await axiosInstance.get(`/api/purchase-scans/${pg.id}/`);
        merged.current.add(pg.id);
        read.push({ id: pg.id, page_no: pg.page_no, lines: d.parsed?.lines || [], total: d.parsed?.total ?? null, cost: d.parsed?.ai?.cost_lkr || 0 });
      }
      resumeExtra.current = read;
      setExtraPages(pages.filter((x) => x.page_no > 1).map((x) => ({ id: x.id, page_no: x.page_no, status: x.status, image_url: x.image_url })));
      setActivePage(0);
      setScan(data);
    } catch (e) { handleApiError(e); } finally { setBusy(false); }
  };

  const discardBill = async (row) => {
    if (!window.confirm('Discard this bill photo? It cannot be recovered.')) return;
    try { await axiosInstance.delete(`/api/purchase-scans/${row.id}/`); loadPending(); } catch (e) { handleApiError(e); }
  };

  const setLine = (i, patch) => setLines((ls) => ls.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const removeLine = (i) => setLines((ls) => ls.filter((_, k) => k !== i));

  // the same rules as the Create Purchase Order dialog (product, quantity, both prices, expiry for expiring
  // products, supplier and invoice number on credit) plus basic range checks
  const lineErrs = useMemo(() => lines.map((l) => {
    const e = {};
    if (!l.product_id) e.product_id = 'Choose the product';
    if (!(l.qty > 0)) e.qty = 'Enter the quantity';
    if (!(l.price > 0)) e.price = 'Enter the price';
    if (!(l.selling_price > 0)) e.selling_price = 'Enter the selling price';
    if (l.units_per_pack != null && l.units_per_pack !== '' && !(l.units_per_pack >= 1)) e.units_per_pack = 'At least 1';
    if (l.free != null && l.free < 0) e.free = 'Cannot be negative';
    if (l.discount_pct != null && (l.discount_pct < 0 || l.discount_pct > 100)) e.discount_pct = '0 to 100';
    if (l.expiry && !validDate(l.expiry)) e.expiry = 'Use YYYY-MM-DD';
    else if (l.expiry && ymdToDate(l.expiry) < minExpiryDate) e.expiry = `Min date: ${localYmd(minExpiryDate)}`;
    else if (!l.expiry && productInfo.get(l.product_id)?.is_expiring) e.expiry = 'Expiry date is required';
    return e;
  }), [lines, productInfo, minExpiryDate]);
  const headErrs = useMemo(() => {
    const e = {};
    if (!form.supplier) e.supplier = 'Choose the supplier';
    if (form.credit_days && !(form.invoice_no || '').trim()) e.invoice_no = 'Required when receiving on credit';
    return e;
  }, [form]);
  const errCount = Object.keys(headErrs).length + lineErrs.reduce((n, e) => n + Object.keys(e).length, 0);

  const linesTotal = lines.reduce((s, l) => s + (l.qty || 0) * (l.price || 0) * (1 - (l.discount_pct || 0) / 100), 0);
  const mismatch = billTotal != null && Math.abs(linesTotal - billTotal) > Math.max(1, 0.002 * billTotal);

  // live check while reviewing: is this supplier + invoice number already a GRN / purchase order or another waiting scan?
  useEffect(() => {
    setDupMsg('');
    if (!scan || scan.status !== 'ready' || !form.supplier || !(form.invoice_no || '').trim()) return undefined;
    const t = setTimeout(() => {
      axiosInstance.post('/api/purchase-scans/check-invoice/', { supplier: form.supplier, invoice_no: form.invoice_no, scan: scan.id })
        .then((r) => setDupMsg(r.data.duplicate ? r.data.message : '')).catch(() => {});
    }, 600);
    return () => clearTimeout(t);
  }, [scan?.id, scan?.status, form.supplier, form.invoice_no]);

  const create = async () => {
    setShowErrors(true);
    if (errCount > 0) {
      handleApiError({ Error: `${errCount} field${errCount > 1 ? 's' : ''} need attention before the draft can be created` });
      setTimeout(() => document.querySelector('.p-invalid, [data-invalid="1"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      return;
    }
    setBusy(true);
    try {
      const body = { supplier: form.supplier, invoice_no: form.invoice_no, credit_days: form.credit_days, items: lines };
      let data;
      try {
        ({ data } = await axiosInstance.post(`/api/purchase-scans/${scan.id}/confirm/`, body));
      } catch (e) {
        if (e?.response?.status === 409 && e.response.data?.error === 'duplicate_invoice') {
          if (!window.confirm(`${e.response.data.message}\n\nCreate another draft GRN for it anyway?`)) return;
          ({ data } = await axiosInstance.post(`/api/purchase-scans/${scan.id}/confirm/`, { ...body, confirm_duplicate: true }));
        } else { throw e; }
      }
      handleApiSuccess({ data });
      onCreated && onCreated(data);
      onHide();
    } catch (e) { handleApiError(e); } finally { setBusy(false); }
  };

  const [zoom, setZoom] = useState(100);          // bill photo zoom, percent of the pane width
  const zoomBy = (d) => setZoom((z) => Math.min(500, Math.max(50, z + d)));
  useEffect(() => { setZoom(100); }, [scan?.id]);

  // Ctrl + wheel zooms the photo, not the whole page. React's onWheel is passive, so preventDefault only
  // works on a native, non-passive listener.
  const paneRef = useRef(null);
  const [panning, setPanning] = useState(false);
  const readyNow = scan?.status === 'ready';
  useEffect(() => {
    const el = paneRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      zoomBy(e.deltaY < 0 ? 25 : -25);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [readyNow]);

  // drag the photo with the mouse to move around it
  const startPan = (e) => {
    if (e.button !== 0) return;
    const el = paneRef.current;
    const start = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
    setPanning(true);
    const move = (ev) => { el.scrollLeft = start.left - (ev.clientX - start.x); el.scrollTop = start.top - (ev.clientY - start.y); };
    const up = () => { setPanning(false); window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    e.preventDefault();
  };
  const [wide, setWide] = useState(typeof window === 'undefined' ? true : window.innerWidth >= 992);
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= 992);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Enter moves to the next field, like the purchase order screen: supplier > invoice no. > credit days > line 1
  // (product, packs, units/pack, free, price, discount, selling price, expiry) > line 2 ... > Create
  const LINE_ORDER = ['product', 'qty', 'units', 'free', 'price', 'disc', 'selling', 'expiry'];
  const focusNav = (id) => setTimeout(() => {
    const el = document.getElementById(id);
    if (!el) return;
    el.focus();
    if (typeof el.select === 'function') el.select();
    el.scrollIntoView?.({ block: 'nearest' });
  }, 0);
  const firstOfLine = (i) => `nav-${i}-${lines[i]?.product_id ? 'qty' : 'product'}`;
  const nextAfter = (i, field) => {
    if (i === 'h') {                                  // header row
      if (field === 'supplier') return focusNav('nav-h-invoice');
      if (field === 'invoice') return focusNav('nav-h-credit');
      return lines.length ? focusNav(firstOfLine(0)) : undefined;
    }
    const idx = LINE_ORDER.indexOf(field);
    if (idx < LINE_ORDER.length - 1) return focusNav(`nav-${i}-${LINE_ORDER[idx + 1]}`);
    return i + 1 < lines.length ? focusNav(firstOfLine(i + 1)) : focusNav('scan-create');
  };
  const enterNav = (i, field) => (ev) => {
    if (ev.key !== 'Enter') return;
    ev.preventDefault();
    ev.currentTarget?.blur?.();                       // commits a number before moving on
    nextAfter(i, field);
  };
  // a dropdown with its list open uses Enter to pick the highlighted entry; only move on when it is closed
  const dropdownEnter = (i, field) => (ev) => {
    if (ev.key !== 'Enter' || document.querySelector('.p-dropdown-panel')) return;
    ev.preventDefault();
    nextAfter(i, field);
  };

  const reading = scan && ['queued', 'processing'].includes(scan.status);
  const ready = scan?.status === 'ready';

  return (
    <Dialog header="Scan supplier bill" visible={visible} onHide={onHide} className="p-dialog-maximized"
            style={{ width: '100vw', height: '100vh', maxHeight: '100%', margin: 0, borderRadius: 0 }}
            contentStyle={{ padding: 0, flex: 1, minHeight: 0, overflow: 'hidden' }} modal>
      {!ready && <div style={{ height: '100%', overflowY: 'auto' }}>
      {!scan && (
        <div className="flex flex-column align-items-center gap-3 p-5">
          <p className="m-0">Take or choose a photo of the supplier invoice. The system reads it and fills in a draft GRN for you to check.</p>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => upload(e.target.files?.[0])} />
          <div className="flex flex-wrap gap-2 justify-content-center">
            <Button icon="pi pi-camera" label="Choose photo" loading={busy} onClick={() => fileRef.current?.click()} />
            <input ref={heldFileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={(e) => uploadHeld(e.target.files)} />
            <Button icon="pi pi-cloud-upload" label="Upload only - read later" outlined loading={busy} onClick={() => heldFileRef.current?.click()} />
          </div>
          <label className="flex align-items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
            <Checkbox inputId="scan-one-page" checked={onePage} onChange={(ev) => setOnePage(ev.checked)} />
            <span>When several photos are chosen for "Upload only", they are pages of one bill</span>
          </label>
          {aiStatus?.enabled && (aiStatus.usable
            ? (
              <label className="flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                <Checkbox inputId="scan-use-ai" checked={useAi} onChange={(ev) => setUseAi(ev.checked)} />
                <span><i className="pi pi-sparkles mr-1" />Read with AI (costs money{aiStatus.balance_lkr != null ? `, wallet ${rs(aiStatus.balance_lkr)}` : ''})</span>
              </label>
            )
            : <Tag icon="pi pi-exclamation-triangle" severity="warning" value={aiStatus.unusable_reason} />)}
          {pending.length > 0 && (
            <div style={{ width: '100%', maxWidth: 900 }} className="mt-3">
              <h4 className="mt-0 mb-2">Waiting bills</h4>
              {pending.map((r) => (
                <div key={r.id} className="flex align-items-center gap-3 p-2 mb-2 border-1 surface-border border-round">
                  {r.image_url ? <img src={r.image_url} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} /> : null}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{r.supplier || r.original_name || `Bill ${r.id}`}{r.invoice_no ? ` - ${r.invoice_no}` : ''}</div>
                    <div className="text-color-secondary text-sm">
                      {r.pages} page{r.pages > 1 ? 's' : ''} - {new Date(r.created_at).toLocaleString()}{r.by ? ` - ${r.by}` : ''}
                      {r.status === 'ready' ? ` - ${r.line_count} lines read` : ''}
                    </div>
                  </div>
                  <Tag severity={{ uploaded: 'info', ready: 'success', failed: 'danger' }[r.status] || 'warning'}
                       value={{ uploaded: 'Not read yet', ready: 'Ready to review', failed: 'Could not read', queued: 'Reading', processing: 'Reading' }[r.status] || r.status} />
                  <Button size="small" label={r.status === 'uploaded' ? 'Read' : r.status === 'failed' ? 'Read again' : r.status === 'ready' ? 'Review' : 'Open'} onClick={() => openBill(r)} disabled={busy} />
                  <Button size="small" icon="pi pi-trash" text severity="danger" aria-label="Discard" onClick={() => discardBill(r)} />
                </div>
              ))}
              {pending.some((r) => r.status === 'uploaded') && aiStatus?.enabled && <small className="text-color-secondary">"Read" uses the AI choice above.</small>}
            </div>
          )}
        </div>
      )}

      {reading && (
        <div className="flex flex-column align-items-center gap-2 p-5">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }} />
          <span>Reading the bill... this can take up to a minute.</span>
        </div>
      )}

      {scan?.status === 'failed' && (
        <div className="p-4">
          <Tag severity="danger" value="Could not read this bill" />
          <p>{scan.error}</p>
          <Button label="Try another photo" onClick={() => setScan(null)} />
        </div>
      )}

      {scan?.status === 'created' && (
        <div className="p-4">Draft GRN {scan.move_order_number} was already created from this bill.</div>
      )}
      </div>}

      {ready && (
        <div style={{ display: 'flex', flexDirection: wide ? 'row' : 'column', height: '100%' }}>
          <div style={{ flex: wide ? '0 0 30%' : '0 0 35%', minHeight: 0, display: 'flex', flexDirection: 'column', background: '#f4f4f4', borderRight: wide ? '1px solid #ddd' : 0 }}>
            <div className="flex align-items-center gap-1 p-2" style={{ borderBottom: '1px solid #ddd', background: '#fff' }}>
              <Button icon="pi pi-search-minus" text rounded aria-label="Zoom out" disabled={zoom <= 50} onClick={() => zoomBy(-25)} />
              <span style={{ minWidth: 48, textAlign: 'center', fontSize: '0.85rem' }}>{zoom}%</span>
              <Button icon="pi pi-search-plus" text rounded aria-label="Zoom in" disabled={zoom >= 500} onClick={() => zoomBy(25)} />
              <Button label="Fit" text size="small" aria-label="Fit to width" onClick={() => setZoom(100)} />
              <span className="ml-auto text-color-secondary" style={{ fontSize: '0.8rem' }}><i className="pi pi-arrows-alt mr-1" />Drag to move</span>
            </div>
            {extraPages.length > 0 && (
              <div className="flex flex-wrap gap-1 p-2" style={{ borderBottom: '1px solid #ddd', background: '#fff' }}>
                {[{ page_no: 1, status: 'ready' }, ...extraPages].map((pg, idx) => (
                  <Button key={pg.id || 'first'} type="button" size="small" outlined={activePage !== idx}
                          icon={['queued', 'processing'].includes(pg.status) ? 'pi pi-spin pi-spinner' : pg.status === 'failed' ? 'pi pi-times' : undefined}
                          severity={pg.status === 'failed' ? 'danger' : undefined} label={`Page ${pg.page_no}`}
                          onClick={() => { setActivePage(idx); setZoom(100); }} />
                ))}
              </div>
            )}
            <div ref={paneRef} onMouseDown={startPan}
                 style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 12, cursor: panning ? 'grabbing' : 'grab', userSelect: 'none' }}>
              <img src={activePage === 0 ? scan.image_url : extraPages[activePage - 1]?.image_url} alt="Bill" draggable={false}
                   style={{ width: `${zoom}%`, maxWidth: 'none', display: 'block', borderRadius: 4, border: '1px solid #ddd' }} />
            </div>
          </div>

          <div style={{ flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', padding: '14px 18px' }}>
            <div className="flex flex-wrap gap-3 align-items-start mb-3">
              <Field label="Supplier *" error={showErrors && headErrs.supplier} style={{ minWidth: 260 }}>
                <div onKeyDown={dropdownEnter('h', 'supplier')}>
                  <Dropdown value={form.supplier} options={suppliers} optionLabel="name" optionValue="id" filter inputId="nav-h-supplier"
                            className={showErrors && headErrs.supplier ? 'p-invalid' : ''} style={{ width: '100%' }}
                            placeholder="Choose supplier" onChange={(e) => setForm({ ...form, supplier: e.value })} />
                </div>
              </Field>
              <Field label={`Supplier invoice no.${form.credit_days ? ' *' : ''}`} error={showErrors && headErrs.invoice_no}>
                <InputText id="nav-h-invoice" value={form.invoice_no} className={showErrors && headErrs.invoice_no ? 'p-invalid' : ''}
                           onKeyDown={enterNav('h', 'invoice')} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} />
              </Field>
              <Field label="Credit days">
                <InputNumber inputId="nav-h-credit" value={form.credit_days} onValueChange={(e) => setForm({ ...form, credit_days: e.value })}
                             useGrouping={false} onKeyDown={enterNav('h', 'credit')} />
              </Field>
              <Field label="Bill date (read)">
                <span style={{ padding: '0.6rem 0' }}>{scan.parsed.invoice_date || '—'}{scan.parsed.date_ambiguous ? ' (day/month unclear)' : ''}</span>
              </Field>
            </div>

            {dupMsg && <div><Tag icon="pi pi-exclamation-triangle" severity="danger" className="mb-1" value={`Possible duplicate: ${dupMsg}`} /></div>}
            {scan.parsed.reader === 'ai' && (() => {
              const total = (scan.parsed.ai?.cost_lkr || 0) + Object.values(pageCosts).reduce((a, b) => a + b, 0);
              return <div><Tag icon="pi pi-sparkles" severity="info" className="mb-1" value={`Read with AI - cost so far ${rs(total)}`} /></div>;
            })()}
            {scan.parsed.reader !== 'ai' && scan.parsed.ai?.reason && <div><Tag severity="warning" className="mb-1" value={scan.parsed.ai.reason} /></div>}
            {(scan.parsed.warnings || []).map((w) => <div key={w}><Tag severity="warning" value={w} className="mb-1" /></div>)}

            <div className="flex flex-column gap-3 mt-2">
              {lines.map((l, i) => {
                const e = (showErrors && lineErrs[i]) || {};
                const bad = Object.keys(e).length > 0;
                return (
                  <div key={i} data-invalid={bad ? '1' : undefined}
                       style={{ border: `1px solid ${bad ? '#e24c4c' : '#d9d9d9'}`, borderLeft: `5px solid ${bad ? '#e24c4c' : '#d9d9d9'}`, borderRadius: 8, padding: '12px 14px', background: '#fff' }}>
                    <div className="flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '0.8rem', color: '#777' }}>Line {i + 1} - as printed{extraPages.length > 0 ? ` - page ${l.page || 1}` : ''}</div>
                        <div style={{ fontWeight: 600, wordBreak: 'break-word' }}>{l.description}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(l.flags || []).filter((f) => FLAG_TEXT[f]).map((f) => <Tag key={f} severity="warning" value={FLAG_TEXT[f]} />)}
                        </div>
                      </div>
                      <Button icon="pi pi-trash" text severity="danger" aria-label="Remove line" onClick={() => removeLine(i)} />
                    </div>

                    <Field label={`Product *${l.product_id && (l.candidates || []).find((c) => c.product_id === l.product_id) ? ` (${Math.round(l.candidates.find((c) => c.product_id === l.product_id).score * 100)}% match)` : ''}`}
                           error={e.product_id} className="mb-3">
                      <div onKeyDown={dropdownEnter(i, 'product')}>
                        <Dropdown value={l.product_id} options={productOptions} filter virtualScrollerOptions={{ itemSize: 38 }} inputId={`nav-${i}-product`}
                                  className={e.product_id ? 'p-invalid' : ''} placeholder="Choose product" style={{ width: '100%' }}
                                  onChange={(ev) => selectProduct(i, ev.value)} />
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {(l.candidates || []).map((c) => (
                          <Button key={c.product_id} type="button" size="small" outlined={l.product_id !== c.product_id}
                                  label={`${c.name} - ${Math.round(c.score * 100)}%`} onClick={() => selectProduct(i, c.product_id)} />
                        ))}
                      </div>
                    </Field>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px 14px' }}>
                      <Field label="Packs *" error={e.qty}>
                        <InputNumber inputId={`nav-${i}-qty`} onKeyDown={enterNav(i, 'qty')} value={l.qty} onValueChange={(ev) => setLine(i, { qty: ev.value })} inputClassName={e.qty ? 'p-invalid' : ''} inputStyle={{ width: '100%' }} />
                      </Field>
                      <Field label="Units per pack" error={e.units_per_pack}>
                        <InputNumber inputId={`nav-${i}-units`} onKeyDown={enterNav(i, 'units')} value={l.units_per_pack} onValueChange={(ev) => setLine(i, { units_per_pack: ev.value })} inputClassName={e.units_per_pack ? 'p-invalid' : ''} inputStyle={{ width: '100%' }} />
                        {l.pack_hint && l.pack_hint !== l.units_per_pack && (
                          <Button type="button" size="small" text label={`Retail price suggests ${l.pack_hint}`} onClick={() => setLine(i, { units_per_pack: l.pack_hint })} />
                        )}
                      </Field>
                      <Field label="Free packs" error={e.free}>
                        <InputNumber inputId={`nav-${i}-free`} onKeyDown={enterNav(i, 'free')} value={l.free} onValueChange={(ev) => setLine(i, { free: ev.value })} inputClassName={e.free ? 'p-invalid' : ''} inputStyle={{ width: '100%' }} />
                      </Field>
                      <Field label="Pack price *" error={e.price}>
                        <InputNumber inputId={`nav-${i}-price`} onKeyDown={enterNav(i, 'price')} value={l.price} onValueChange={(ev) => setLine(i, { price: ev.value })} mode="decimal" minFractionDigits={2} inputClassName={e.price ? 'p-invalid' : ''} inputStyle={{ width: '100%' }} />
                      </Field>
                      <Field label="Discount %" error={e.discount_pct}>
                        <InputNumber inputId={`nav-${i}-disc`} onKeyDown={enterNav(i, 'disc')} value={l.discount_pct} onValueChange={(ev) => setLine(i, { discount_pct: ev.value })} inputClassName={e.discount_pct ? 'p-invalid' : ''} inputStyle={{ width: '100%' }} />
                      </Field>
                      <Field label="Selling price *" error={e.selling_price}>
                        <InputNumber inputId={`nav-${i}-selling`} onKeyDown={enterNav(i, 'selling')} value={l.selling_price} onValueChange={(ev) => setLine(i, { selling_price: ev.value })} mode="decimal" minFractionDigits={2}
                                     inputClassName={e.selling_price ? 'p-invalid' : ''} inputStyle={{ width: '100%' }} />
                      </Field>
                      <Field label={`Expiry${productInfo.get(l.product_id)?.is_expiring ? ' *' : ''}`} error={e.expiry}>
                        <div className={e.expiry ? 'p-invalid' : ''} style={{ borderRadius: 6, outline: e.expiry ? '1px solid #e24c4c' : 'none' }}>
                          <ExpiryInput id={`nav-${i}-expiry`} value={ymdToDate(l.expiry)} minDate={minExpiryDate} style={{ width: '100%' }}
                                       onChange={(d) => setLine(i, { expiry: toYmd(d) })}
                                       onCommit={(d) => { if (d) nextAfter(i, 'expiry'); }}
                                       onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); nextAfter(i, 'expiry'); } }} />
                        </div>
                      </Field>
                      <Field label="Line value">
                        <span style={{ padding: '0.6rem 0', fontWeight: 600 }}>{money((l.qty || 0) * (l.price || 0) * (1 - (l.discount_pct || 0) / 100))}</span>
                      </Field>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-wrap justify-content-between align-items-center mt-3 gap-3"
                 style={{ position: 'sticky', bottom: -14, background: '#fff', padding: '10px 0', borderTop: '1px solid #ddd', zIndex: 2 }}>
              <div>
                Lines total: <b>{money(linesTotal)}</b>{billTotal != null && <> &nbsp;|&nbsp; Bill total: <b>{money(billTotal)}</b></>}
                {mismatch && linesTotal < billTotal && !readingPage && <Tag className="ml-2" severity="warning" icon="pi pi-file" value="Lines total is below the bill total - is there another page?" />}
                {mismatch && !(linesTotal < billTotal) && <Tag className="ml-2" severity="danger" value="Totals differ - check every line" />}
                {readingPage && <Tag className="ml-2" severity="info" icon="pi pi-spin pi-spinner" value="Reading the next page..." />}
                {showErrors && errCount > 0 && <Tag className="ml-2" severity="danger" icon="pi pi-exclamation-circle" value={`${errCount} field${errCount > 1 ? 's' : ''} need attention`} />}
              </div>
              <div className="flex gap-2">
                <input ref={pageFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => addPage(e.target.files?.[0])} />
                <Button label="Add next page" icon="pi pi-file-plus" outlined disabled={busy || readingPage} onClick={() => pageFileRef.current?.click()} />
                <Button label="Cancel" text onClick={onHide} />
                <Button id="scan-create" label="Create draft GRN" icon="pi pi-check" loading={busy} disabled={!lines.length || readingPage} onClick={create} />
              </div>
            </div>
            <small className="block mt-2 text-color-secondary">
              The draft is not received yet. Open it under Move Orders, check batch and expiry, then receive it. The bill photo stays attached to the draft.
            </small>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default PurchaseScanDialog;
